import Store  from '@eurobits/js-store'
import jwt    from 'jsonwebtoken'

let STORE

// Creates a singleton instance of EJStore ASAP
((w) => {
  STORE = STORE || new Store()
  STORE.engine = w.sessionStorage
})(window)

/**
 * Prefixes `efactura_` into store key
 * @param   {String}  text  Key to prefix
 * @return  {String}  The text with `efactura_` prefix
 */
const prefix = text =>
  /^efactura_/.test(text) ? text : `efactura_${text}`

/**
 * Saves all values into store
 * @param {Object}  data      To be store
 * @param {Boolean} support   Whether is support session or not
 * @param {Boolean} external  Whether is an external login or not
 */
export const save = (data = {}, support = false, external = false) => {
  STORE.setItem(prefix('token'), data.token)
  STORE.setSecItem(prefix('user_document'), data.userDocument)
  STORE.setItem(prefix('language'), data.language)
  STORE.setItem(prefix('support_session'), support)
  STORE.setItem(prefix('external_login'), external)
  STORE.setItem(prefix('login_api'), false)
  if (support) {
    STORE.setItem(prefix('app'), data.app)
    STORE.setItem(prefix('environment'), data.environment)
    if (data.supportUser) {
      STORE.setSecItem(prefix('supportUser'), data.supportUser)
      STORE.setSecItem(prefix('supportPass'), data.supportPass)
    }
  }
  STORE.dump()
}

/**
 * Saves new critical values into store
 * @param {Object}  data      To be store
 * @param {Boolean} external  Whether if is an external login or not
 */
export const saveNew = (data = {}, external = false) => {
  const dataToken = JSON.parse(atob(data.access_token.split('.')[1]))
  STORE.setItem(prefix('token'), data.access_token)
  STORE.setItem(prefix('refresh_token'), data.refresh_token)
  STORE.setItem(prefix('external_login'), external)
  STORE.setItem(prefix('login_api'), true)
  STORE.setSecItem(prefix('username'), dataToken.data.username)
  STORE.setSecItem(prefix('user_document'), dataToken.data.registeredEntityNIF)
  STORE.dump()
}

/**
 * Saves critical support values from a support session
 * @param {Object}  data  To be store
 */
export const saveSupport = (data = {}) => {
  const dataToken = JSON.parse(atob(data.access_token.split('.')[1]))
  STORE.setItem(prefix('external_login'), false)
  STORE.setItem(prefix('login_api'), true)
  STORE.setItem(prefix('support_session'), true)
  STORE.setItem(prefix('token'), data.access_token)
  STORE.setItem(prefix('refresh_token'), data.refresh_token)
  STORE.setSecItem(prefix('user_document'), dataToken.data.registeredEntityNIF)

  // estos campos solo se guardan en la llamada al token, no en el refresh
  const other = ['environment', 'app']
  other.filter(key => key in data)
    .forEach(key => STORE.setItem(prefix(key), data[key]))

  const secured = ['supportUser', 'supportPass']
  secured.filter(key => key in data)
    .forEach(key => STORE.setSecItem(prefix(key), data[key]))

  if (data.impersonateUsername) {
    STORE.setSecItem(prefix('username'), data.impersonateUsername)
  }

  STORE.dump()
}

/**
 * Complete clears the stored values
 */
export const destroy = () =>
  STORE.clear(true)

/**
 * Replaces current stored values with new ones
 * @param {*}       data    New data to store
 * @param {Boolean} support Whether if is under support portal
 */
export const replace = (data, support = false) => {
  destroy()
  save(data, support)
}

/**
 * Returns current session token
 * @return {String|undefined} Session token or 'undefined' in other case
 */
export const getToken = () =>
  STORE.getItem(prefix('token'))

/**
 * Returns current refresh session token
 * @return {String|undefined} Refresh token or 'undefined' in other case
 */
export const getRefreshToken = () =>
  STORE.getItem(prefix('refresh_token'))

/**
 * Returns an item from store
 * @param {String}  item  Unique item identifier
 * @return {*|undefined}  Stored value or 'undefined' in other case
 */
export const getValue = item =>
  STORE.getItem(prefix(item))

/**
 * Returns a secured item from store
 * @param   {String}  item  Unique item identifier
 * @return  {*|undefined} Stored secured value or 'undefined' in other case
 */
export const getSecValue = item =>
  STORE.getSecItem(prefix(item))

/**
 * Returns current user ID
 * @return {String} User ID
 */
export const getUserId = () => {
  const { payload = {}} = jwt.decode(getToken(), { complete: true })
  const { id = '' } = payload.data || {}

  return id
}

/**
 * Returns current user role
 * @return {*|String} Current user role
 */
export const getUserRole = () => {
  const { payload = {}} = jwt.decode(getToken(), { complete: true })
  const { authorities = null } = payload

  return authorities
    ? authorities[0]
    : 'ROLE_ADMIN'
}

/**
 * Determines if current user roles as admin.
 * Available roles are:
 * - ROLE_ADMIN
 * - ROLE_BASIC
 * @return {boolean}
 */
export const isAdmin = () =>
  ['ROLE_ADMIN'].includes(getUserRole())

/**
 * Returns whether is a support session or not
 * @return {Boolean} `true` if is a support session, 'false' in other case
 */
export const isSupportSession = () =>
  !!STORE.getItem(prefix('support_session'))

/**
 * Returns whether is an external session or not
 * @return {Boolean} `true` if is an external session, `false` in other case
 */
export const isExternalSession = () =>
  !!STORE.getItem(prefix('external_login'))

/**
 * Stores the authentication token
 * @param {*} token To be store
 */
export const setTokenAuth = token =>
  STORE.setItem(prefix('auth_token'), token)

/**
 * Returns the current authentication token value
 * @return {*|Boolean} Current authentication token or `false` if is empty
 */
export const getTokenAuth = () =>
  STORE.getItem(prefix('auth_token')) || false

/**
 * Returns whether application requires an API login
 * @return {Boolean} `true` if application requires API login, `false` in other case
 */
export const isApiLogin = () =>
  !!STORE.getItem(prefix('login_api'))

export const getDueToExpire = () =>
  STORE.getItem(prefix('expiry'))

export const setBackToken = token => {
  STORE.setItem(prefix('token'), token)
}
